import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = 'fex_portal_token';

const PortalContext = createContext(null);

export const usePortal = () => {
  const ctx = useContext(PortalContext);
  if (!ctx) throw new Error('usePortal must be used inside PortalProvider');
  return ctx;
};

export const PortalProvider = ({ children }) => {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [member, setMember] = useState(null);
  const [loading, setLoading] = useState(true);

  const refreshMember = useCallback(async (tk) => {
    const authToken = tk || token;
    if (!authToken) {
      setMember(null);
      setLoading(false);
      return null;
    }
    try {
      const res = await axios.get(`${API}/portal/me`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      setMember(res.data);
      return res.data;
    } catch (err) {
      // Invalid/expired token — logout
      localStorage.removeItem(TOKEN_KEY);
      setToken(null);
      setMember(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    refreshMember();
  }, [refreshMember]);

  const login = async (email, password) => {
    const res = await axios.post(`${API}/portal/auth/login`, { email, password });
    const tk = res.data.access_token;
    localStorage.setItem(TOKEN_KEY, tk);
    setToken(tk);
    await refreshMember(tk);
    return res.data;
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setMember(null);
  };

  const authHeaders = () => ({ Authorization: `Bearer ${token}` });

  return (
    <PortalContext.Provider value={{ token, member, loading, login, logout, refreshMember, authHeaders }}>
      {children}
    </PortalContext.Provider>
  );
};
